import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { useSession } from "./SessionContext";
import axiosInstance from "../api/axiosConfig";
import ApiConfig from "../api/config";

const PermissionContext = createContext(null);

export const usePermissions = () => {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return ctx;
};

/**
 * GET /GetPermissions → string[]
 * e.g. ["business.add", "terminal.upsert", ...]
 */
const fetchMyPermissions = async () => {
  const response = await axiosInstance.get(ApiConfig.ENDPOINTS.GET_MY_PERMISSIONS);

  // Backend returns a plain array of permission strings.
  if (Array.isArray(response.data)) {
    return response.data;
  }

  // Defensive fallbacks for alternate envelope shapes.
  const payload = response.data;
  if (payload && Array.isArray(payload.permissions)) return payload.permissions;
  if (payload && Array.isArray(payload.data)) return payload.data;

  console.warn("[permissions] Unexpected response shape:", payload);
  return [];
};

export const PermissionProvider = ({ children }) => {
  const { isAuthenticated, session } = useSession();

  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const perms = await fetchMyPermissions();
      setPermissions(Array.isArray(perms) ? perms : []);
    } catch (err) {
      console.error("[permissions] Failed to load:", err);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Re-fetch whenever the session changes (login/logout/refresh).
  // `session?.token` is the trigger — it changes on every login/refresh.
  useEffect(() => {
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, session?.token]);

  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  const hasPermission = useCallback(
    (permission) => permissionSet.has(permission),
    [permissionSet],
  );

  const hasAllPermissions = useCallback(
    (perms = []) => perms.every((p) => permissionSet.has(p)),
    [permissionSet],
  );

  const hasAnyPermission = useCallback(
    (perms = []) => perms.some((p) => permissionSet.has(p)),
    [permissionSet],
  );

  const value = {
    permissions,
    loading,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    refresh: loadPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};